import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { fetchAllPaginated } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';

/**
 * Escolha da turma para abrir a Grade Horária ou o Diário.
 *
 * Antes, os dois custavam três cliques e um modal: Turmas -> clicar na turma
 * -> detalhes -> o botão. Para o professor esse é o caminho de todo dia,
 * porque é da grade que ele entra na chamada da própria aula.
 *
 * Com uma turma só, nem escolha existe: vai direto para ela.
 *
 * `destino` é o que muda entre os dois itens do menu — a tela é a mesma.
 */
function GradeHorariaTurmas({ destino = 'grade-horaria', titulo = 'Grade Horária' }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { darkMode } = useTheme();

  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [busca, setBusca] = useState('');

  const ehProfessor = user?.role === ROLES.PROFESSOR;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      // A API já devolve só as turmas que este usuário alcança: o professor
      // recebe as dele, a coordenação recebe as do município.
      const lista = await fetchAllPaginated('/classes/', {});
      const ordenada = [...lista].sort((a, b) =>
        (a.class_name || '').localeCompare(b.class_name || '', 'pt-BR')
      );
      setTurmas(ordenada);
      if (ordenada.length === 1) {
        navigate(`/turmas/${ordenada[0].id}/${destino}`, { replace: true });
      }
    } catch (e) {
      console.error('Erro ao carregar turmas:', e);
      setErro('Não foi possível carregar as suas turmas.');
    } finally {
      setLoading(false);
    }
  }, [navigate, destino]);

  useEffect(() => { carregar(); }, [carregar]);

  const filtradas = turmas.filter(t => {
    if (!busca.trim()) return true;
    const alvo = `${t.class_name} ${t.school_name || ''}`.toLowerCase();
    return alvo.includes(busca.toLowerCase());
  });

  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  if (loading) return <LoadingSpinnerAdvanced />;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-6">
        <h1 className={`text-2xl font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
          {titulo}
        </h1>
        <p className={`text-sm mt-1 ${muted}`}>
          {destino === 'diario'
            ? 'Escolha a turma para registrar aulas, frequência e notas.'
            : ehProfessor
            ? 'Escolha a turma. No quadro, clique numa aula sua para abrir a chamada.'
            : 'Escolha a turma para montar ou revisar o quadro de horários.'}
        </p>
      </header>

      {erro && (
        <div className="mb-4 p-4 rounded-xl border border-[#E83A3C]/30 bg-[#E83A3C]/10 text-[#E83A3C] text-sm">
          {erro}
        </div>
      )}

      {turmas.length > 6 && (
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar turma ou escola..."
          className={`w-full mb-5 px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
            darkMode
              ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
              : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
          }`}
        />
      )}

      {turmas.length === 0 ? (
        <div className={`rounded-xl border border-dashed p-8 text-center ${
          darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-500'
        }`}>
          <p className="text-sm">
            {ehProfessor
              ? 'Você ainda não está vinculado a nenhuma turma. Fale com a coordenação da escola.'
              : 'Nenhuma turma cadastrada para o seu escopo.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtradas.map(turma => (
            <button
              key={turma.id}
              onClick={() => navigate(`/turmas/${turma.id}/${destino}`)}
              className={`text-left p-4 rounded-xl border transition-all hover:border-[#0974BA] hover:shadow-lg hover:shadow-[#0974BA]/10 ${
                darkMode
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-white border-slate-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className={`font-semibold truncate ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                    {turma.class_name}
                  </div>
                  <div className={`text-xs mt-0.5 truncate ${muted}`}>
                    {turma.school_name || '—'}
                  </div>
                  <div className={`text-xs mt-1.5 ${muted}`}>
                    {[turma.shift, turma.school_year].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <svg className="w-5 h-5 flex-shrink-0 text-[#0974BA]" fill="none"
                     stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      )}

      {filtradas.length === 0 && turmas.length > 0 && (
        <p className={`text-sm mt-4 ${muted}`}>Nenhuma turma encontrada para “{busca}”.</p>
      )}
    </div>
  );
}

export default GradeHorariaTurmas;
